import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaUser, FaUsers, FaComments, FaStar, FaClipboardCheck } from 'react-icons/fa';
import { FaArrowRightFromBracket, FaLocationDot } from 'react-icons/fa6';
import { useSupervisorAuth } from '../../../lib/AuthProvider';
import { supabase } from '../../../lib/supabaseClient';
import '../../OJTCoordinator/OJTCoordinator.css';
import '../SupervisorHome.css';
import './SupervisorFeedback.css';

interface FeedbackForm {
  traineeId: string;
  rating: number;
  comment: string;
}

interface Trainee {
  id: string;
  first_name: string;
  last_name: string;
  student_id: string;
  department: string;
}

const SupervisorFeedback: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { supervisor, loading: authLoading, signOut } = useSupervisorAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [feedbackForm, setFeedbackForm] = useState<FeedbackForm>({
    traineeId: '',
    rating: 0,
    comment: ''
  });
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [trainees, setTrainees] = useState<Trainee[]>([]);
  const [traineesLoading, setTraineesLoading] = useState(false);
  const [hoveredRating, setHoveredRating] = useState(0);

  // Check session validity on component mount
  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error || !session) {
          console.log('Session invalid in SupervisorFeedback, redirecting to login');
          navigate('/', { replace: true });
          return;
        }
        
        if (!supervisor && !authLoading) {
          console.log('No supervisor data but session exists, waiting for AuthProvider to load...');
          setTimeout(() => {
            if (!supervisor && !authLoading) {
              console.log('Still no supervisor data after timeout, redirecting to login');
              navigate('/', { replace: true });
            }
          }, 2000);
        }
      } catch (error) {
        console.error('Error checking session in SupervisorFeedback:', error);
      }
    };

    if (!authLoading) {
      checkSession();
    }
  }, [supervisor, authLoading, navigate]);

  // Fetch trainees when supervisor is loaded
  useEffect(() => {
    if (supervisor?.office) {
      fetchTrainees(supervisor.office);
    }
  }, [supervisor?.office]);

  // Fetch trainees from the supervisor's office
  const fetchTrainees = async (office: string) => {
    setTraineesLoading(true);
    try {
      const { data: traineesData, error } = await supabase
        .from('student_profile')
        .select('id, first_name, last_name, student_id, department')
        .eq('department', office)
        .order('last_name');

      if (error) {
        console.error('Error fetching trainees:', error);
        return;
      }

      setTrainees(traineesData || []);
    } catch (error) {
      console.error('Error fetching trainees:', error);
    } finally {
      setTraineesLoading(false);
    }
  };

  // Handle feedback form submission (static for now)
  const handleFeedbackSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!supervisor) {
      alert('Supervisor information not available. Please try again.');
      return;
    }

    // Validate required fields
    if (!feedbackForm.traineeId || feedbackForm.rating === 0 || !feedbackForm.comment.trim()) {
      alert('Please fill in all required fields including rating.');
      return;
    }

    setFeedbackLoading(true);
    
    // Simulate submission delay (static for now)
    setTimeout(() => {
      // Reset form
      setFeedbackForm({
        traineeId: '',
        rating: 0,
        comment: ''
      });
      setFeedbackLoading(false);
      alert('Feedback submitted successfully! (Static demo)');
    }, 1000);
  };

  // Handle form input changes
  const handleFeedbackFormChange = (field: keyof FeedbackForm, value: string | number) => {
    setFeedbackForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle rating click
  const handleRatingClick = (rating: number) => {
    setFeedbackForm(prev => ({
      ...prev,
      rating
    }));
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <div className="coordinator-home">
      <header className="top-nav">
        <div className="nav-container">
          <div className="logo">
            <h1>InternTr<FaLocationDot className="location-icon" />ckr</h1>
          </div>
          <button
            className="hamburger-btn"
            aria-label="Open menu"
            aria-controls="mobile-sidebar"
            aria-expanded={isMenuOpen}
            onClick={() => setIsMenuOpen(true)}
          >
            {'\u2630'}
          </button>
          <div className="nav-links">
            <button 
              onClick={() => navigate('/supervisor/home')} 
              className={`nav-link ${isActive('/supervisor/home') ? 'active' : ''}`}
            >
              <FaUsers className="nav-icon" /> My Trainees
            </button>
            <button 
              onClick={() => navigate('/supervisor/attendance')} 
              className={`nav-link ${isActive('/supervisor/attendance') ? 'active' : ''}`}
            >
              <FaClipboardCheck className="nav-icon" /> Attendance
            </button>
            <button 
              onClick={() => navigate('/supervisor/feedback')} 
              className={`nav-link ${isActive('/supervisor/feedback') ? 'active' : ''}`}
            >
              <FaComments className="nav-icon" /> Feedback
            </button>
            <button 
              onClick={() => navigate('/supervisor/profile')} 
              className={`nav-link ${isActive('/supervisor/profile') ? 'active' : ''}`}
            >
              <FaUser className="nav-icon" /> Profile
            </button>
          </div>
          <button onClick={handleSignOut} className="nav-link logout-button">
            <FaArrowRightFromBracket className="nav-icon" /> Sign Out
          </button>
        </div>
      </header>

      {/* Mobile sidebar and backdrop */}
      <div
        className={`backdrop${isMenuOpen ? ' open' : ''}`}
        onClick={() => setIsMenuOpen(false)}
        aria-hidden={!isMenuOpen}
      />
      <aside
        id="mobile-sidebar"
        className={`sidebar${isMenuOpen ? ' open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        <div className="sidebar-header">
          <h2>Menu</h2>
          <button
            className="close-btn"
            aria-label="Close menu"
            onClick={() => setIsMenuOpen(false)}
          >
            {'\u00D7'}
          </button>
        </div>
        <nav className="sidebar-links" onClick={() => setIsMenuOpen(false)}>
          <button 
            onClick={() => navigate('/supervisor/home')} 
            className="nav-link"
          >
            <FaUsers className="nav-icon" /> My Trainees
          </button>
          <button 
            onClick={() => navigate('/supervisor/attendance')} 
            className="nav-link"
          >
            <FaClipboardCheck className="nav-icon" /> Attendance
          </button>
          <button 
            onClick={() => navigate('/supervisor/feedback')} 
            className="nav-link"
          >
            <FaComments className="nav-icon" /> Feedback
          </button>
          <button 
            onClick={() => navigate('/supervisor/profile')} 
            className="nav-link"
          >
            <FaUser className="nav-icon" /> Profile
          </button>
        </nav>
        <div className="sidebar-footer">
          <button 
            onClick={(e) => {
              e.stopPropagation();
              handleSignOut();
            }} 
            className="sidebar-logout-button"
          >
            <FaArrowRightFromBracket className="nav-icon" /> Sign Out
          </button>
        </div>
      </aside>

      <main className="main-content">
        <div className="welcome-section">
          <h1>Trainee Feedback</h1>
          {supervisor?.office && (
            <p className="office-info">Office: {supervisor.office}</p>
          )}
        </div>

        {authLoading ? (
          <div className="feedback-loading">
            <p>Loading...</p>
          </div>
        ) : supervisor ? (
          <div className="feedback-section">
            <div className="feedback-form-container">
              <form onSubmit={handleFeedbackSubmit} className="feedback-form">
                <div className="form-group">
                  <label htmlFor="trainee">Select Trainee</label>
                  <select
                    id="trainee"
                    value={feedbackForm.traineeId}
                    onChange={(e) => handleFeedbackFormChange('traineeId', e.target.value)}
                    className="form-select"
                    disabled={traineesLoading}
                    required
                  >
                    <option value="">
                      {traineesLoading 
                        ? "Loading trainees..." 
                        : trainees.length === 0 
                          ? "No trainees in your office"
                          : "Select Trainee"
                      }
                    </option>
                    {trainees.map((trainee) => (
                      <option key={trainee.id} value={trainee.id}>
                        {trainee.first_name} {trainee.last_name} - {trainee.student_id}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Performance Rating</label>
                  <div className="rating-container">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        className={`star-button ${star <= (hoveredRating || feedbackForm.rating) ? 'active' : ''}`}
                        onClick={() => handleRatingClick(star)}
                        onMouseEnter={() => setHoveredRating(star)}
                        onMouseLeave={() => setHoveredRating(0)}
                        aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`}
                      >
                        <FaStar />
                      </button>
                    ))}
                    <span className="rating-text">
                      {feedbackForm.rating > 0 ? `${feedbackForm.rating} / 5` : 'Select rating'}
                    </span>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="comment">Feedback Comments</label>
                  <textarea
                    id="comment"
                    value={feedbackForm.comment}
                    onChange={(e) => handleFeedbackFormChange('comment', e.target.value)}
                    className="form-textarea"
                    placeholder="Provide feedback about the trainee's performance, strengths, areas for improvement..."
                    rows={5}
                    required
                  />
                </div>

                <div className="form-actions">
                  <button
                    type="submit"
                    disabled={feedbackLoading || trainees.length === 0}
                    className="submit-button"
                  >
                    {feedbackLoading ? 'Sending...' : 'Submit Feedback'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        ) : (
          <div className="feedback-error">
            <p>Unable to load feedback form. Please try again later.</p>
            <button 
              className="action-button" 
              onClick={() => navigate('/supervisor/home')}
            >
              Return to Home
            </button>
          </div>
        )}
      </main>
    </div>
  );
};

export default SupervisorFeedback;
